import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, MenuItem, Typography } from '@mui/material';
import { InputField } from '../../../components/InputController';
import { useFormik } from 'formik';
import CustomDropdown from '../../../components/customDropdown';
import {
  ApplicationQuestionAndAnswerDto,
  CustomQuestionTypeEnum,
  CustomQuestionResponseTypeEnum,
  ApplicationQuestionResponseV2Dto,
  UpdateAddressDTO,
  UpsertApplicantRentalHistoryV2Dto,
} from '@inosio/payments-sdk';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { applicationStyles } from './CustomStyles';
import { InosioDateFormat } from '../../../shared/util/formaters';
import { convertSqlDateToUSCalendarDate, convertUSCalendarDateToSqlDate } from '../../../shared/util/converters';
import { approvedISOStandards } from '../../../shared/util/approvedISOStandards';
import { useQuery } from '../../../pmui/transactions/Transactions';
import { useParams } from 'react-router';
import { useEffect, useState } from 'react';
import { getRentalHistoryRecord, selectApplicantsState, updateCustomQuestionsAnswersV2, updateRentalHistoryRecord } from '../sagas/applicants/applicantsSlice';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import CustomQuestionsSection from './CustomQuestionsSection';
import * as Yup from 'yup';
import { DobRegex, E164PhoneRegex, EmailRegex } from '../../../utils/regex';
import { PhoneNumberInput } from '../../../shared/PhoneNumberInput';

const moveInDateValidation = Yup.string()
  .min(10, 'Please enter a date in this format MM/DD/YYYY')
  .max(10, 'Please enter a date in this format MM/DD/YYYY')
  .matches(DobRegex, 'Please enter a date in this format MM/DD/YYYY')
  .test({
    name: 'Move In Date Check',
    exclusive: true,
    message: 'Move In Date should be less than today',
    test: (value, testContext) => {
      const returnObj = testContext.parent.MoveInDate && new Date(testContext.parent.MoveInDate) > new Date();
      return !returnObj;
    },
  })
  .test({
    name: 'Move In Date Check with Move Out Date',
    exclusive: true,
    message: 'Move In Date should be less than Move Out Date',
    test: (value, testContext) => {
      const returnObj = testContext.parent.MoveInDate && testContext.parent.MoveOutDate && new Date(testContext.parent.MoveInDate) > new Date(testContext.parent.MoveOutDate);
      return !returnObj;
    },
  });

const schemaValidationApplicationRentalHistory = Yup.object({
  Line1: Yup.string().required('Please enter a street address'),
  Line2: Yup.string().nullable(),
  City: Yup.string().required('Please enter a city'),
  State: Yup.string().required('Please select a state / province'),
  Zip: Yup.string().min(5, 'Please enter a five digit postal code').max(5, 'Please enter a five digit postal code').required('Please enter a postal code'),
  Country: Yup.string().required('Please select a country'),
  IsCurrentAddress: Yup.boolean().required(),
  ContactFacilityName: Yup.string().when('RentalHistoryTypeId', {
    is: 1,
    then: Yup.string().required("Please enter the landlord's name"),
    otherwise: Yup.string(),
  }),
  ContactPhone: Yup.string().when('RentalHistoryTypeId', {
    is:3,
    then:  Yup.string(),
    otherwise: Yup.string()
    .matches(E164PhoneRegex, 'Invalid Phone Number. Please enter in this format +[Country Code][Phone Number]')
    .required('Required Phone Number'),
  }),
  ContactEmail: Yup.string().when('RentalHistoryTypeId', {
    is: 3, 
    then: Yup.string(),
    otherwise: Yup.string()
    .matches(EmailRegex, 'Please type a valid Email.')
    .required("Please enter the landlord's Email")
  }),
  MonthlyPayment: Yup.string()
  .when('RentalHistoryTypeId', {
    is: 3,
    then: Yup.string(), 
    otherwise: Yup.string()
      .required('Please enter the monthly rent that you paid'),
  }),
  RentalHistoryTypeId: Yup.number().required(),
  PurchaseDate: Yup.string()
    .min(10, 'Please enter a date in this format MM/DD/YYYY')
    .max(10, 'Please enter a date in this format MM/DD/YYYY')
    .matches(DobRegex, 'Please enter a date in this format MM/DD/YYYY')
    .test({
      name: 'Purchase Date Check',
      exclusive: true,
      message: 'Purchase Date should be less than today',
      test: (value, testContext) => {
        const returnObj = testContext.parent.PurchaseDate && new Date(testContext.parent.PurchaseDate) > new Date();
        return !returnObj;
      },
    }),
    MoveInDate: Yup.string()
  .when('RentalHistoryTypeId', {
    is: 3,
    then: moveInDateValidation.optional().nullable(),
    otherwise: moveInDateValidation.required('Please enter a move in date'),
  }),
  MoveOutDate: Yup.string()
  .min(10, 'Please enter a date in this format MM/DD/YYYY')
  .max(10, 'Please enter a date in this format MM/DD/YYYY')
  .matches(DobRegex, 'Please enter a date in this format MM/DD/YYYY')
  .nullable()
  .test({
    name: 'Move Out Date Check',
    exclusive: true,
    message: 'Move Out Date should be less than today',
    test: (value, testContext) => {
      const returnObj = testContext.parent.MoveOutDate && new Date(testContext.parent.MoveOutDate) > new Date();
      return !returnObj;
    },
  })
  .test({
    name: 'Move Out Date Check with Move In Date',
    exclusive: true,
    message: 'Move Out Date should be greater than Move In Date',
    test: (value, testContext) => {
      const returnObj = testContext.parent.MoveInDate && testContext.parent.MoveOutDate && new Date(testContext.parent.MoveInDate) > new Date(testContext.parent.MoveOutDate);
      return !returnObj;
    },
  }),
});


type schemaValidationApplicationRentalHistoryType = Yup.InferType<typeof schemaValidationApplicationRentalHistory>;

//These line ub with the DB, please DO NOT change
const applicationRentalHistoryTypes = [
  {
    Key: 'I Rent/Rented',
    Value: 1,
  },
  {
    Key: 'I Own/Owned',
    Value: 2,
  },
  {
    Key: 'Other',
    Value: 3,
  },
];

export default function ApplicationRentalHistory() {
  const query = useQuery();
  const hasValidationQueryParam = !!query.get('validation');
  const { id: rentalHistoryUuidParam } = useParams<{ id: string }>();
  const classes = applicationStyles();
  const dispatch = useDispatch();

  //selectors
  const { ipaUser } = useSelector(selectAuthState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);
  const {
    Application,
    isFetchingCustomQuestionAnswersSuccess,
    isFetchingCustomQuestionAnswersFailure,
    CustomQuestionAnswers,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isFetchingRentalHistoryRecordFailed,
    isFetchingRentalHistoryRecordSuccess,
    fetchedRentalHistoryRecord,
    isUpdatingRentalHistoryRecordFailed,
    isUpdatingRentalHistoryRecordSuccess,
    updateRentalHistoryRecordError,
  } = useSelector(selectApplicantsState);
  const { Countries: approvedCountries, States: approvedStates, Provinces: approvedProvinces } = approvedISOStandards;

  //local state
  const [localError, setLocalError] = useState<string | null>(null);
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [currentRentalHistoryUuid] = useState(rentalHistoryUuidParam);
  const [questionsAndAnswers, setQuestionsAndAnswers] = useState<Array<ApplicationQuestionAndAnswerDto>>([]);
  const [initialFormValues, setInitialFormValues] = useState<schemaValidationApplicationRentalHistoryType>({
    Line1: '',
    Line2: '',
    City: '',
    State: '',
    Zip: '',
    Country: 'US',
    ContactFacilityName: '',
    ContactPhone: '',
    ContactEmail: '',
    MonthlyPayment: '',
    MoveInDate: '',
    MoveOutDate: '',
    IsCurrentAddress: false,
    RentalHistoryTypeId: 1, //default should be a Rental
    PurchaseDate: '',
  });

  const formik = useFormik<schemaValidationApplicationRentalHistoryType>({
    initialValues: initialFormValues,
    validationSchema: schemaValidationApplicationRentalHistory,
    onSubmit: async (values) => {},
    enableReinitialize: true,
    validateOnMount: true,
  });

  const resetStateSelection = () => {
    formik.setFieldValue('State', '');
  };

  const handleMoveInDate = (MoveInDate: string) => {
    const formattedMoveInDate = InosioDateFormat(MoveInDate);
    formik.setFieldValue('MoveInDate', formattedMoveInDate);
  };

  const handleMoveOutDate = (MoveOutDate: string) => {
    const formattedMoveOutDate = InosioDateFormat(MoveOutDate);
    formik.setFieldValue('MoveOutDate', formattedMoveOutDate);
  };

  const handlePurchaseDate = (PurchaseDate: string) => {
    const formattedMoveOutDate = InosioDateFormat(PurchaseDate);
    formik.setFieldValue('PurchaseDate', formattedMoveOutDate);
  };

  const handleCustomQuestionFieldChanged = (e: any, questionUuid: string) => {
    setQuestionsAndAnswers(
      questionsAndAnswers.map((qa) => {
        if (qa.Uuid === questionUuid) {
          //found our question.

          if (qa.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean) {
            return {
              ...qa,
              BooleanQuestionAnswer: e.target.value === 'true',
            };
          } else
            return {
              ...qa,
              TextQuestionAnswer: e.target.value,
            };
        }
        return qa;
      }),
    );
  };

  const handleSaveCustomQuestionAnswer = (e: any, questionUuid: string) => {
    const answeredQuestion = questionsAndAnswers.find((q) => q.Uuid === questionUuid);

    if (answeredQuestion) {
      dispatch({
        type: updateCustomQuestionsAnswersV2.type,
        payload: {
          applicationUuid: Application.Uuid,
          upsertDto: {
            QuestionUuid: answeredQuestion?.Uuid,
            ResponseType: answeredQuestion?.CustomQuestionResponseType,
            Response: answeredQuestion.CustomQuestionResponseType === CustomQuestionResponseTypeEnum.Boolean ? e.target.value === 'true' : e.target.value,
            BelongsToUuid: currentRentalHistoryUuid,
            ApplicantUuid: ipaUser?.currentProfile.uuid,
          } as ApplicationQuestionResponseV2Dto,
        },
      });
    }
  };

  const handleFormValuesChanged = (e: any) => {
    /*
     */

    const addressPart = {
      City: !formik.errors.City ? formik.values.City || undefined : undefined,
      State: !formik.errors.State ? formik.values.State || undefined : undefined,
      Zip: !formik.errors.Zip ? formik.values.Zip || undefined : undefined,
      Line1: !formik.errors.Line1 ? formik.values.Line1 || undefined : undefined,
      Line2: !formik.errors.Line2 ? formik.values.Line2 || undefined : undefined,
      Country: !formik.errors.Country ? formik.values.Country || undefined : undefined,
    } as UpdateAddressDTO;

    const payload = {
      MonthlyPayment: !formik.errors.MonthlyPayment ? parseInt(formik.values.MonthlyPayment || '0') || undefined : undefined,
      ContactFacilityName: !formik.errors.ContactFacilityName ? formik.values.ContactFacilityName || undefined : undefined,
      ContactPhone: !formik.errors.ContactPhone ? formik.values.ContactPhone || undefined : undefined,
      ContactEmail: !formik.errors.ContactEmail ? formik.values.ContactEmail || undefined : undefined,
      MoveInDate: !formik.errors.MoveInDate ? (formik.values.MoveInDate ? convertUSCalendarDateToSqlDate(formik.values.MoveInDate) : undefined) : undefined,
      MoveOutDate: !formik.errors.MoveOutDate ? (formik.values.MoveOutDate ? convertUSCalendarDateToSqlDate(formik.values.MoveOutDate) : null) : undefined,
      PurchaseDate: !formik.errors.PurchaseDate ? (formik.values.PurchaseDate ? convertUSCalendarDateToSqlDate(formik.values.PurchaseDate) : undefined) : undefined,
      IsCurrentAddress: !formik.errors.IsCurrentAddress ? formik.values.IsCurrentAddress : undefined,
      Address: addressPart,
    } as UpsertApplicantRentalHistoryV2Dto;

    for (const [key] of Object.entries(payload)) {
      if ((payload as any)[key] === undefined) {
        delete (payload as any)[key];
      }
    }

    dispatch({
      type: updateRentalHistoryRecord.type,
      payload: {
        applicantUuid: ipaUser?.currentProfile.uuid,
        rentalHistoryUuid: currentRentalHistoryUuid,
        updateDto: payload,
      },
    });
  };

  const handleRentalTypeChange = (rentalHistoryTypeId: number) => {
    //set rental history type
    formik.setFieldValue('RentalHistoryTypeId', rentalHistoryTypeId);

    //reset these fields because rental history type changed
    formik.setFieldValue('ContactFacilityName', '');
    formik.setFieldValue('ContactPhone', '');
    formik.setFieldValue('ContactEmail', '');
    formik.setFieldValue('PurchaseDate', '');
    formik.touched.ContactFacilityName = false;

    //set the new rental history type, also null out existing data that might be related to the previous rental type
    dispatch({
      type: updateRentalHistoryRecord.type,
      payload: {
        applicantUuid: ipaUser?.currentProfile.uuid,
        rentalHistoryUuid: currentRentalHistoryUuid,
        updateDto: {
          RentalHistoryTypeId: rentalHistoryTypeId,
          ContactEmail: null,
          ContactFacilityName: null,
          ContactPhone: null,
          PurchaseDate: null,
        },
      },
    });
  };

  //#region useffects

  //on load fetch the rental history record that is to be edited
  useEffect(() => {
    if (ipaUser?.currentProfile.uuid) {
      dispatch({
        type: getRentalHistoryRecord.type,
        payload: {
          applicantUuid: ipaUser?.currentProfile.uuid,
          rentalHistoryUuid: rentalHistoryUuidParam,
        },
      });
    }
  }, [dispatch, ipaUser]);

  //set the initial local state with the fetched data
  useEffect(() => {
    if (isFetchingRentalHistoryRecordSuccess) {
      setInitialFormValues({
        ...initialFormValues,
        Line1: fetchedRentalHistoryRecord.Address?.Line1 ?? '',
        Line2: fetchedRentalHistoryRecord.Address?.Line2 ?? '',
        City: fetchedRentalHistoryRecord.Address?.City ?? '',
        State: fetchedRentalHistoryRecord.Address?.State ?? '',
        Zip: fetchedRentalHistoryRecord.Address?.Zip ?? '',
        Country: fetchedRentalHistoryRecord.Address?.Country || 'US',
        ContactFacilityName: fetchedRentalHistoryRecord.ContactFacilityName ?? '',
        ContactPhone: fetchedRentalHistoryRecord.ContactPhone ?? '',
        ContactEmail: fetchedRentalHistoryRecord.ContactEmail ?? '',
        MonthlyPayment: fetchedRentalHistoryRecord.MonthlyPayment ? `${fetchedRentalHistoryRecord.MonthlyPayment}` : '',
        MoveInDate: fetchedRentalHistoryRecord.MoveInDate ? convertSqlDateToUSCalendarDate(fetchedRentalHistoryRecord.MoveInDate) : '',
        MoveOutDate: fetchedRentalHistoryRecord.MoveOutDate ? convertSqlDateToUSCalendarDate(fetchedRentalHistoryRecord.MoveOutDate) : '',
        IsCurrentAddress: fetchedRentalHistoryRecord.IsCurrentAddress ?? false,
        RentalHistoryTypeId: fetchedRentalHistoryRecord.RentalHistoryTypeId ?? 1,
        PurchaseDate: fetchedRentalHistoryRecord.PurchaseDate ? convertSqlDateToUSCalendarDate(fetchedRentalHistoryRecord.PurchaseDate) : '',
      });
    }
  }, [isFetchingRentalHistoryRecordSuccess]);

  //populate custom questions once prop group details received
  useEffect(() => {
    if (propertyGroupDetails?.Uuid && isFetchingCustomQuestionAnswersSuccess) {
      const organizationRentalHistoryCustomQuestions = (propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsQuestions ?? []).filter(
        (q) => q.CustomQuestionType === CustomQuestionTypeEnum.RentalHistoryV1,
      );
      setQuestionsAndAnswers(
        organizationRentalHistoryCustomQuestions.map((q) => {
          const previouslySavedResponseToThisQuestion = CustomQuestionAnswers?.find((a) => a.QuestionUuid === q.Uuid && a.BelongsToUuid === currentRentalHistoryUuid);

          const question: ApplicationQuestionAndAnswerDto = {
            ...q,
            ApplicantUuid: previouslySavedResponseToThisQuestion?.ApplicantUuid!,
            BelongsToEntityUuid: currentRentalHistoryUuid,
          };

          switch (question.CustomQuestionResponseType) {
            case CustomQuestionResponseTypeEnum.FreeText:
              question.TextQuestionAnswer = previouslySavedResponseToThisQuestion?.Response?.toString();
              break;
            case CustomQuestionResponseTypeEnum.Boolean:
              question.BooleanQuestionAnswer = previouslySavedResponseToThisQuestion?.Response === undefined ? undefined : Boolean(previouslySavedResponseToThisQuestion?.Response);
              break;
            default:
              break;
          }

          return question;
        }),
      );
    }
  }, [propertyGroupDetails, isFetchingCustomQuestionAnswersSuccess]);

  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess && isFetchingRentalHistoryRecordSuccess && isFetchingApplicationSuccess && isFetchingCustomQuestionAnswersSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (isFetchingPropertyGroupDetailsFailed || isFetchingRentalHistoryRecordFailed || isFetchingApplicationFailure || isFetchingCustomQuestionAnswersFailure) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading rental history information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [
    isFetchingPropertyGroupDetailsSuccess,
    isFetchingPropertyGroupDetailsFailed,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isFetchingCustomQuestionAnswersSuccess,
    isFetchingCustomQuestionAnswersFailure,
    isFetchingRentalHistoryRecordFailed,
    isFetchingRentalHistoryRecordSuccess,
  ]);

  //show error if rental history update failed
  useEffect(() => {
    if (isUpdatingRentalHistoryRecordFailed) {
      setLocalError(updateRentalHistoryRecordError.message ?? 'There was an error saving your information.');
    }
    if (isUpdatingRentalHistoryRecordSuccess) {
      setLocalError(null);
    }
  }, [isUpdatingRentalHistoryRecordFailed, isUpdatingRentalHistoryRecordSuccess]);
  
  return (
    <Box sx={{ width: '100%', p: 4 }}>
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Grid container spacing={4}>
          {localError && (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
              <Typography className={classes.globalText}>{localError}</Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Grid container xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
              <Grid item xs={5}>
                <Typography className={classes.cardBodyText} mr={2}>
                  Occupancy Type:
                </Typography>
              </Grid>
              <Grid item xs={7}>
                <CustomDropdown
                  isFullWidth={false}
                  value={String(formik.values.RentalHistoryTypeId)}
                  error={hasValidationQueryParam && !formik.values.RentalHistoryTypeId ? 'Please select an option' : undefined}
                  label={'Select Type'}
                  showAsterisk={!true}
                  handleChange={(e) => {
                    handleRentalTypeChange(Number(e.target.value));
                  }}
                  handleBlur={(e) => {
                    formik.handleBlur(e);
                  }}
                  name="RentalHistoryTypeId"
                  menuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: '24px',
                      },
                    },
                  }}
                  customStyle={{
                    marginBottom: '0',
                  }}
                >
                  {applicationRentalHistoryTypes.map((type) => (
                    <MenuItem key={type.Key} value={type.Value}>
                      {type.Key}
                    </MenuItem>
                  ))}
                </CustomDropdown>
              </Grid>
            </Grid>
          </Grid>
          <Grid item sm={6}></Grid>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ mb: 2 }} className={classes.labelText}>
              Physical Address:
            </Typography>
            <InputField
              label={'Address Line 1'}
              isError={!formik.values?.Line1 && hasValidationQueryParam}
              errors={formik.errors?.Line1}
              touched={formik.touched?.Line1}
              {...formik.getFieldProps('Line1')}
              onBlur={(e) => {
                formik.handleBlur(e);
                handleFormValuesChanged(e);
              }}
              showAsterisk={!formik.values.Line1}
            />
            <InputField
              label={'Address Line 2'}
              isError={!!formik.errors?.Line2 && hasValidationQueryParam && hasValidationQueryParam}
              errors={formik.errors?.Line2}
              touched={formik.touched?.Line2}
              {...formik.getFieldProps('Line2')}
              onBlur={(e) => {
                formik.handleBlur(e);
                handleFormValuesChanged(e);
              }}
            />
            <Grid container direction="row" spacing={1}>
              <Grid item xs={12} id="city-input">
                <InputField
                  label={'City'}
                  isError={!formik.values?.City && hasValidationQueryParam}
                  errors={formik.errors?.City}
                  touched={formik.touched?.City}
                  {...formik.getFieldProps('City')}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleFormValuesChanged(e);
                  }}
                  showAsterisk={!formik.values.City}
                />
              </Grid>
              <Grid item xs={12} id="state-input">
                {formik.values?.Country && ['US', 'CA'].includes(formik.values?.Country) ? (
                  <>
                    <CustomDropdown
                      id={'state-dropdown'}
                      isFullWidth
                      label={'State / Province'}
                      labelId={''}
                      name="State"
                      value={formik.values.State}
                      error={hasValidationQueryParam ? formik.errors?.State : undefined}
                      handleChange={(e) => {
                        formik.setFieldValue('State', e.target.value);
                      }}
                      menuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: '24px',
                          },
                        },
                      }}
                      showAsterisk={!formik.values.State}
                      handleBlur={(e) => {
                        formik.handleBlur(e);
                        handleFormValuesChanged(e);
                      }}
                    >
                      {formik.values?.Country === 'US'
                        ? approvedStates.map((stateRecord) => (
                            <MenuItem style={{ height: '36px' }} key={stateRecord.Code} value={stateRecord.Code}>
                              {stateRecord.State + ' - ' + stateRecord.Code}
                            </MenuItem>
                          ))
                        : approvedProvinces.map((provinceRecord) => (
                            <MenuItem style={{ height: '36px' }} key={provinceRecord.Code} value={provinceRecord.Code}>
                              {provinceRecord.Province + ' - ' + provinceRecord.Code}
                            </MenuItem>
                          ))}
                    </CustomDropdown>

                    {/* Below standard select is just fake and only for autofill. Ramsey would kill me. */}
                    <select id={'state-dropdown-alt'} name="State" value={formik.values?.State || ''} onChange={(e) => formik.setFieldValue('State', e.target.value)} style={{ display: 'none' }}>
                      {formik.values?.Country === 'US'
                        ? approvedStates.map((stateRecord) => (
                            <option key={stateRecord.Code} value={stateRecord.Code}>
                              {stateRecord.State + ' - ' + stateRecord.Code}
                            </option>
                          ))
                        : approvedProvinces.map((provinceRecord) => (
                            <option key={provinceRecord.Code} value={provinceRecord.Code}>
                              {provinceRecord.Province + ' - ' + provinceRecord.Code}
                            </option>
                          ))}
                    </select>
                  </>
                ) : (
                  <InputField
                    label={'State / Province'}
                    isError={!!formik.errors?.State && hasValidationQueryParam}
                    errors={formik.errors?.State}
                    touched={formik.touched?.State}
                    showAsterisk={!formik.values.State}
                    {...formik.getFieldProps('State')}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      handleFormValuesChanged(e);
                    }}
                  />
                )}
              </Grid>
            </Grid>
            <Grid container direction="row" spacing={1}>
              <Grid item xs={12} sm={6} id="zip-input">
                <InputField
                  label={'Postal Code'}
                  isError={!!formik.errors?.Zip && hasValidationQueryParam}
                  errors={formik.errors?.Zip}
                  touched={formik.touched?.Zip}
                  showAsterisk={!formik.values.Zip}
                  {...formik.getFieldProps('Zip')}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleFormValuesChanged(e);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} id="countries-dropdown">
                <CustomDropdown
                  label={'Country'}
                  isFullWidth
                  handleChange={(e: any) => {
                    resetStateSelection();
                    formik.setFieldValue('Country', e.target.value);
                  }}
                  value={formik.values?.Country || ''}
                  menuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: '24px',
                      },
                    },
                  }}
                  showAsterisk={true}
                  handleBlur={handleFormValuesChanged}
                >
                  <MenuItem style={{ height: '36px' }} value="">
                    {`Select Country`}
                  </MenuItem>
                  {approvedCountries.map((countryRecord) => (
                    <MenuItem style={{ height: '36px' }} key={countryRecord.Code} value={countryRecord.Code}>
                      {countryRecord.Country + ' - ' + countryRecord.Code}
                    </MenuItem>
                  ))}
                </CustomDropdown>
              </Grid>
            </Grid>
          </Grid>
          {formik.values.RentalHistoryTypeId === 2 ? (
            <Grid item xs={12} sm={6}>
              <Typography sx={{ mb: 2 }} className={classes.labelText}>
                Mortgage Details:
              </Typography>
              <InputField
                label={'Lender/ Bank Name'}
                isError={!!formik.errors?.ContactFacilityName && hasValidationQueryParam}
                errors={formik.errors?.ContactFacilityName}
                touched={formik.touched?.ContactFacilityName}
                showAsterisk={formik.values.RentalHistoryTypeId !== 2}
                {...formik.getFieldProps('ContactFacilityName')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
              />
              <InputField
                label={'Purchase Date'}
                placeholder="MM/DD/YYYY"
                isError={!formik.values?.PurchaseDate && hasValidationQueryParam}
                errors={formik.errors?.PurchaseDate}
                touched={formik.touched?.PurchaseDate}
                {...formik.getFieldProps('PurchaseDate')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                onChange={(e) => {
                  handlePurchaseDate(e.target.value);
                }}
                showAsterisk={!formik.values.PurchaseDate}
              />
            </Grid>
          ) : (
            <Grid item xs={12} sm={6}>
              <Typography sx={{ mb: 2 }} className={classes.labelText}>
                Property Manager/ Landlord Information:
              </Typography>
              <InputField
                label={'Property Manager/ Landlord Name'}
                isError={!!formik.errors?.ContactFacilityName && hasValidationQueryParam && formik.values.RentalHistoryTypeId !== 3}
                errors={formik.errors?.ContactFacilityName}
                touched={formik.touched?.ContactFacilityName}
                showAsterisk={!formik.values.ContactFacilityName && formik.values.RentalHistoryTypeId !== 3}
                {...formik.getFieldProps('ContactFacilityName')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
              />
              <PhoneNumberInput
                {...formik.getFieldProps('ContactPhone')}
                showErrorMessage={!!formik.errors?.ContactPhone && !!formik.touched?.ContactPhone}
                onChange={(phone) => {
                  formik.setFieldValue('ContactPhone', phone.startsWith('+') ? phone : `+${phone}`);
                }}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
                errorMessage={formik.errors?.ContactPhone}
                showErrorPinkBackground={!!formik.errors?.ContactPhone && hasValidationQueryParam}
                showAsterisk={formik.values.RentalHistoryTypeId !== 3}
              />
              {/*Adding margins to phoneinput above is making it wierd so adding it here, do not remove */}
              <Box sx={{ marginTop: '16px' }}></Box>
              <InputField
                label={'Email Address'}
                isError={!!formik.errors?.ContactEmail && hasValidationQueryParam && formik.values.RentalHistoryTypeId !== 3}
                errors={formik.errors?.ContactEmail}
                touched={formik.touched?.ContactEmail}
                showAsterisk={!formik.values.ContactEmail && formik.values.RentalHistoryTypeId !== 3}
                {...formik.getFieldProps('ContactEmail')}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  handleFormValuesChanged(e);
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Typography sx={{ mb: 2 }} className={classes.labelText}>
              Occupancy Details:
            </Typography>
            <InputField
              label={'Monthly Payment Amount'}
              isError={!!formik.errors?.MonthlyPayment && hasValidationQueryParam && formik.values.RentalHistoryTypeId !== 3}
              errors={formik.errors?.MonthlyPayment}
              touched={formik.touched?.MonthlyPayment}
              showAsterisk={!formik.values.MonthlyPayment && formik.values.RentalHistoryTypeId !== 3}
              {...formik.getFieldProps('MonthlyPayment')}
              onBlur={(e) => {
                formik.handleBlur(e);
                handleFormValuesChanged(e);
              }}
              onChange={(e) => {
                const input = e.target.value;
                const newValue = input.replace(/[^0-9]/g, ''); // Remove non-numeric characters
                formik.setFieldValue('MonthlyPayment', newValue);
              }}
            />
            <Grid container>
              <Grid item xs={12}>
                <InputField
                  label={'Move In Date'}
                  placeholder="MM/DD/YYYY"
                  isError={!formik.values?.MoveInDate && hasValidationQueryParam && formik.values.RentalHistoryTypeId !== 3}
                  errors={formik.errors?.MoveInDate}
                  touched={formik.touched?.MoveInDate}
                  {...formik.getFieldProps('MoveInDate')}
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    handleFormValuesChanged(e);
                  }}
                  onChange={(e) => handleMoveInDate(e.target.value)}
                  showAsterisk={!formik.values.MoveInDate && formik.values.RentalHistoryTypeId !== 3}
                />
              </Grid>
            </Grid>
            <Grid container>
              {!fetchedRentalHistoryRecord.IsCurrentAddress && (
                <Grid item xs={12}>
                  <InputField
                    label={'Move Out Date'}
                    placeholder="MM/DD/YYYY"
                    isError={!formik.values?.MoveOutDate && !formik.values.IsCurrentAddress === true && hasValidationQueryParam && formik.values.RentalHistoryTypeId !== 3}
                    errors={formik.errors?.MoveOutDate}
                    touched={formik.touched?.MoveOutDate}
                    {...formik.getFieldProps('MoveOutDate')}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      handleFormValuesChanged(e);
                    }}
                    onChange={(e) => {
                      handleMoveOutDate(e.target.value);
                      if (e.target.value && !!formik.values.IsCurrentAddress) formik.setFieldValue('IsCurrentAddress', false);
                    }}
                    showAsterisk={!formik.values.MoveOutDate && !formik.values.IsCurrentAddress && formik.values.RentalHistoryTypeId !== 3}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <CustomQuestionsSection
            questionsAndAnswers={questionsAndAnswers}
            hasValidationQueryParam={hasValidationQueryParam}
            onFieldChange={handleCustomQuestionFieldChanged}
            onFieldUpdate={handleSaveCustomQuestionAnswer}
          />
        </Grid>
      )}
    </Box>
  );
}
